/* Import components */

import React, { useCallback, useEffect, useState } from 'react';
import BootstrapTable from 'react-bootstrap-table-next';
import { useHistory } from 'react-router';
import { axios } from '../libs';
import { defaultColumnsOption } from '../config';
import logger from '../utils/logger';

/* Export table */

export const DataTable = ({
	navigateTo,
	url,
	dataKey,
	tableHeading,
	columns,
	onDelete,
	canDelete,
	onCopy,
	onEdit,
	onData,
	onPreview,
	refetch = false,
	actions,
	isPreview = false,
	defaultSorted = [{ dataField: 'createdAt', order: 'desc' }],
	rowClasses,
	rowStyle
}) => {
	const [data, setData] = useState([]);
	const [sortField, setSortField] = useState(defaultSorted[0].dataField); // Track sort field
	const [sortOrder, setSortOrder] = useState(defaultSorted[0].order); // Track sort order
	const { push } = useHistory();
	
	/* Fetch data and sort locally */
	
	const fetchData = useCallback(async () => {
		try {
			// Adjust the limit parameter to fetch more rows
			const response = await axios.get(`${url}?limit=2000`);
			let fetchedData = response[dataKey];
			// Sort the data locally
			fetchedData = sortData(fetchedData, sortField, sortOrder);
			setData(fetchedData);
		} catch (error) {
			logger.error("Error fetching data", error);
			return error;
		}
	}, [dataKey, url, sortField, sortOrder]);
	
	/* Sort the data locally */
	
	const sortData = (data, sortField, sortOrder) => {
		return [...data].sort((a, b) => {
			const aValue = a[sortField];
			const bValue = b[sortField];
			if (aValue < bValue) return sortOrder === 'asc' ? -1 : 1;
			if (aValue > bValue) return sortOrder === 'asc' ? 1 : -1;
			return 0;
		});
	};
	
	/* Initial data fetch */
	
	useEffect(() => { fetchData(); }, [fetchData]);
	
	/* Refetch data if `refetch` prop changes */
	
	useEffect(() => { if (refetch) { fetchData(); } }, [refetch, fetchData]);
	
	/* Handle table change events for sorting */
	
	const onTableChange = (type, { sortField, sortOrder }) => {
		if (type === 'sort') {
			setSortField(sortField);
			setSortOrder(sortOrder);
		}
	};
	
	/* Handle search input change */
	
	const onSearch = async ({ target: { value } }) => {
		try {
			const updatedUrl = `${url}?name=${value}`;
			const response   = await axios.get(updatedUrl);
			let searchedData = response[dataKey];
			searchedData     = sortData(searchedData, sortField, sortOrder); // Sort after search
			setData(searchedData);
		} catch (error) {
			logger.error("Error during search", error);
		}
	};
	
	/* Define action column formatter */
	
	const columnDataFormatter = (cell, row, rowIndex, formatExtraData) => {
		
		return (
			<div className="actions">
				{
					onDelete && (!canDelete || canDelete(row)) &&
					<button onClick={() => onDelete(row)} className="button button-small">Delete</button>
				}
				{
					onCopy &&
					<button onClick={() => onCopy(row)} className="button button-small">Duplicate</button>
				}
				{
					onData &&
					<button onClick={() => onData(row)} className="button button-small">Data</button>
				}
				{
					onEdit &&
					<button onClick={() => onEdit(row)} className="button button-small">Edit</button>
				}
				{
					onPreview &&
					<button onClick={() => onPreview(row)} className="button button-small">Preview</button>
				}
			</div>
		);
	};
	
	/* Define action column */
	
	const columnFormatter = {
		dataField: '',
		text: 'Actions',
		classes: 'cell-actions',
		headerClasses: 'cell-actions',
		formatter: columnDataFormatter,
	};
	
	/* Define default sort caret */
	
	const defaultSortCaret = (order, column) => (
		<span className="order">
			<span className={`caret caret-up${order === 'asc' ? ' active' : ''}`} />
			<span className={`caret caret-down${order === 'desc' ? ' active' : ''}`} />
		</span>
	);
	
	/* Define table row click events */
	
	const tableEvents = {
		events: {
			onClick: (e, column, columnIndex, row, rowIndex) => {
				if (!navigateTo) return;
				if (dataKey === 'documents') {
					window.open(`${navigateTo}/${row._id}/edit`, '_blank');
				} else {
					push(`${navigateTo}/${row._id}`);
				}
			},
		},
	};
	
	return (
	<>
		<div className="action-search">
			<input onChange={onSearch} placeholder="Enter keyword…" />
			<i class="fa-solid fa-magnifying-glass"></i>
		</div>
		<BootstrapTable
		remote           = {{ sort: false }} // Disable remote sorting
		hover
		striped
		keyField         = "_id"
		noDataIndication = "No data found"
		data             = {data}
		columns          = {columns
			.map((column) => ({
				...column,
				sort: true, // Ensure sorting is enabled for each column
				sortCaret: column.sortCaret || defaultSortCaret,
				...tableEvents,
				...defaultColumnsOption,
			}))
			.concat({ ...actions, ...columnFormatter })
		}
		classes          = "sheet-table"
		rowClasses       = {rowClasses || "table-row"}
		rowStyle         = {rowStyle}
		onTableChange    = {onTableChange} // Handle sorting locally
		defaultSorted    = {defaultSorted} // Set default sorting options
		/>
	</>
	);
};
